import { Comment as CommentAntD } from '@ant-design/compatible';
import { CheckCircleOutlined } from '@ant-design/icons';
import { List, Skeleton, Space, Tooltip, Typography, theme } from 'antd';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentAutomation from '../hooks/adminPage/automation/useCurrentAutomation';
import useCurrentTrigger from '../hooks/adminPage/triggers/useCurrentTrigger';
import useCurrentTicketEvents from '../hooks/ticketDetailPage/useCurrentTicketEvents';
import { sortEventListByGivenObjectKey } from '../hooks/ticketDetailPage/useEvents';
import { usePaginatedFormsNoQueryParams } from '../hooks/ticketDetailPage/useForms';
import useTicketFieldDefinitionList, {
    getUIFriendlyTicketFieldDefinitionList
} from '../hooks/ticketDetailPage/useTicketFieldDefinitionList';
import useUserById from '../hooks/useUserById';
import { BrowserMetadata, Change, FieldDefinitionMap, TicketSystemFieldsKey } from '../model/types';
import { isAdmin } from '../utils/accountHelper';
import {
    getFormattedDateWithDayjs,
    getFormattedDateWithHourWithDayjs,
    getUserCredentialsToShow
} from '../utils/helpers';
import Comment from './Comment';
import CustomDivider from './CustomDivider';
import { customFieldTypeMap } from './CustomField/CustomField.type';
import ParagraphWithTooltip from './ParagraphWithTooltip';
import ProfilePicture from './ProfilePicture';
import { formatDate } from './TimeDisplay';

const { Text } = Typography;

const eventsSystemFieldKeyi18nKeyMap = {
    'ts.assignee': 'Assignee',
    'ts.attachments': 'Attachments',
    'ts.creator': 'Creator',
    'ts.description': 'Description',
    'ts.email_ccs': 'Email CCs',
    'ts.followers': 'Followers',
    'ts.priority': 'Priority',
    'ts.requester': 'Requester',
    'ts.status': 'Status',
    'ts.subject': 'Subject',
    'ts.tags': 'Tags',
    'ts.type': 'Type',
    'ts.end_user_phone': 'Customer Phone'
} as const;

interface Props {
    item: Change;
    fieldListObject: FieldDefinitionMap;
}

function Item({ item = {} as Change, fieldListObject }: Props) {
    const {
        token: { colorFillSecondary, fontSizeSM, padding }
    } = theme.useToken();

    const { fieldDefinitionKey, previousValue, newValue } = item;
    const { t } = useTranslation('forms');
    const { t: tTickets } = useTranslation('tickets');
    const { t: tCommon } = useTranslation('common');
    const { data: forms, isLoading } = usePaginatedFormsNoQueryParams();

    const isRenderable =
        (!!(previousValue || newValue) && previousValue !== newValue) ||
        fieldDefinitionKey !== TicketSystemFieldsKey.description;
    if (!isRenderable) return null;

    const isFormField = fieldDefinitionKey === TicketSystemFieldsKey.formId;

    const currentValue =
        fieldListObject[fieldDefinitionKey]?.type === customFieldTypeMap.DATE
            ? tCommon(getFormattedDateWithDayjs(newValue) as any)
            : fieldListObject[fieldDefinitionKey]?.type === customFieldTypeMap.DATE_TIME
            ? tCommon(getFormattedDateWithHourWithDayjs(newValue) as any)
            : fieldListObject[fieldDefinitionKey]?.type === customFieldTypeMap.ATTACHMENTS
            ? newValue
                  .split(',')
                  .map((n) => n.split(':')[0])
                  .join(', ')
            : isFormField
            ? forms?.content?.find((f) => f.id === +newValue)?.name ?? t(newValue as any)
            : t(newValue as any);

    const changedValue =
        fieldListObject[fieldDefinitionKey]?.type === customFieldTypeMap.DATE
            ? tCommon(getFormattedDateWithDayjs(previousValue) as any)
            : fieldListObject[fieldDefinitionKey]?.type === customFieldTypeMap.DATE_TIME
            ? tCommon(getFormattedDateWithHourWithDayjs(previousValue) as any)
            : isFormField
            ? forms?.content?.find((f) => f.id === +previousValue)?.name ?? t(previousValue as any)
            : t(previousValue as any);

    const fieldName = eventsSystemFieldKeyi18nKeyMap[fieldDefinitionKey] ?? fieldListObject[fieldDefinitionKey]?.name;
    if (!fieldName) {
        return (
            <List.Item
                style={{
                    borderBottom: 'none',
                    backgroundColor: colorFillSecondary,
                    paddingLeft: padding,
                    paddingTop: padding / 2,
                    paddingBottom: padding / 2,
                    overflowY: 'hidden'
                }}
            >
                <Tooltip placement="left" title={tTickets('fieldDeleted')}>
                    <Space className="full-width" align="start">
                        <CheckCircleOutlined style={{ fontSize: fontSizeSM }} />
                        <Text
                            strong
                            style={{ fontSize: fontSizeSM, whiteSpace: 'nowrap' }}
                        >{`${fieldDefinitionKey} :`}</Text>
                        <Space className="full-width" align="start" wrap>
                            {isFormField && isLoading ? (
                                <Skeleton active paragraph={{ rows: 1, width: 100 }} title={false} />
                            ) : (
                                <>
                                    {currentValue && (
                                        <ParagraphWithTooltip
                                            tooltip={currentValue}
                                            style={{ fontSize: fontSizeSM }}
                                            message
                                        >
                                            {currentValue}
                                        </ParagraphWithTooltip>
                                    )}
                                    {changedValue && (
                                        <ParagraphWithTooltip
                                            tooltip={changedValue}
                                            delete
                                            style={{ fontSize: fontSizeSM }}
                                            message
                                        >
                                            {changedValue}
                                        </ParagraphWithTooltip>
                                    )}
                                </>
                            )}
                        </Space>
                    </Space>
                </Tooltip>
            </List.Item>
        );
    }

    return (
        <List.Item
            style={{
                borderBottom: 'none',
                backgroundColor: colorFillSecondary,
                paddingLeft: padding,
                paddingTop: padding / 2,
                paddingBottom: padding / 2,
                overflowY: 'hidden'
            }}
        >
            <Space className="full-width" align="start">
                <CheckCircleOutlined style={{ fontSize: fontSizeSM }} />
                <Text strong style={{ fontSize: fontSizeSM, whiteSpace: 'nowrap' }}>{`${t(fieldName)} :`}</Text>
                <Space className="full-width" align="start" wrap>
                    {isFormField && isLoading ? (
                        <Skeleton active paragraph={{ rows: 1, width: 100 }} title={false} />
                    ) : (
                        <>
                            {currentValue && (
                                <ParagraphWithTooltip tooltip={currentValue} style={{ fontSize: fontSizeSM }} message>
                                    {currentValue}
                                </ParagraphWithTooltip>
                            )}
                            {changedValue && (
                                <ParagraphWithTooltip
                                    tooltip={changedValue}
                                    delete
                                    style={{ fontSize: fontSizeSM }}
                                    message
                                >
                                    {changedValue}
                                </ParagraphWithTooltip>
                            )}
                        </>
                    )}
                </Space>
            </Space>
        </List.Item>
    );
}
const TriggerLink = ({ triggerId }: { triggerId: number }) => {
    const {
        token: { marginXS, fontSizeSM }
    } = theme.useToken();
    const {
        data: {
            userInfo: { rls }
        }
    } = useSession();
    const isUserAdmin = isAdmin(rls[0]);

    const { data: currentTrigger, isLoading, error } = useCurrentTrigger(triggerId);
    if (isLoading) return <Skeleton.Input size="small" />;
    if (error) return null;
    return (
        <Typography.Link
            type="secondary"
            style={{ fontSize: fontSizeSM - 2, marginRight: marginXS }}
            {...(isUserAdmin ? { href: `/admin/triggers/${currentTrigger.id}` } : {})}
        >
            {currentTrigger.name}
        </Typography.Link>
    );
};

const AutomationLink = ({ automationId, jobId }: { automationId: string; jobId: string }) => {
    const {
        token: { marginXS, fontSizeSM }
    } = theme.useToken();
    const {
        data: {
            userInfo: { rls }
        }
    } = useSession();
    const isUserAdmin = isAdmin(rls[0]);
    const isBulkChange = +automationId < 0;
    const { data: currentAutomation, isLoading, error } = useCurrentAutomation(!isBulkChange ? +automationId : null);
    if (isLoading) return <Skeleton.Input size="small" />;
    if (error) return null;
    if (isBulkChange || !jobId)
        return (
            <Typography.Text type="secondary" style={{ fontSize: fontSizeSM - 2 }}>
                {isBulkChange ? jobId : automationId}
            </Typography.Text>
        );
    return (
        <Typography.Link
            type="secondary"
            style={{ fontSize: fontSizeSM - 2, marginRight: marginXS }}
            {...(isUserAdmin ? { href: `/admin/automations/history?automationId=${automationId}&jobId=${jobId}` } : {})}
        >
            {currentAutomation?.name}
        </Typography.Link>
    );
};

const translatedUserAgentNameList = ['Trigger', 'Automation', 'Bulk Change'];

function BrowserMetaData({ data }: { data: BrowserMetadata }) {
    const {
        token: { marginXS, fontSizeSM }
    } = theme.useToken();

    const { t } = useTranslation('common');

    const triggers = data?.userAgent === 'Trigger' ? data?.ip?.split(',') : [];
    const automation =
        data?.userAgent === 'Automation'
            ? { automationId: data?.ip?.split(':')[0], jobId: data?.ip?.split(':')[1] }
            : {};
    const isBulkChange = data?.userAgent === 'Automation' && +data?.ip?.split(':')[0] < 0;
    const userAgent = isBulkChange ? 'Bulk Change' : data?.userAgent;
    const ip = data?.ip;
    const location = data?.location;
    if (!data) return <Skeleton.Input active size="small" />;
    return (
        <>
            <CustomDivider antdProps={{ style: { marginBottom: marginXS, marginTop: marginXS } }} />
            <Space direction="vertical" size={0} className="full-width">
                {userAgent && (
                    <Text type="secondary" style={{ fontSize: fontSizeSM - 2 }}>
                        {translatedUserAgentNameList.includes(userAgent) ? t(`historyNames.${userAgent}`) : userAgent}
                    </Text>
                )}
                {triggers.length ? (
                    <Space>
                        {triggers.map((trigger) => (
                            <TriggerLink key={trigger} triggerId={+trigger} />
                        ))}
                    </Space>
                ) : automation?.automationId ? (
                    <AutomationLink automationId={automation.automationId} jobId={automation.jobId} />
                ) : (
                    ip && (
                        <Text type="secondary" style={{ fontSize: fontSizeSM - 2 }}>
                            {ip}
                        </Text>
                    )
                )}
                {location && location !== 'N/A' && (
                    <Text type="secondary" style={{ fontSize: fontSizeSM - 2 }}>
                        {location}
                    </Text>
                )}
            </Space>
        </>
    );
}

function Author({ userId }: { userId: number }) {
    const { data, isLoading } = useUserById(userId?.toString());

    if (isLoading) return <Skeleton.Input size="small" active className="full-width" />;
    return <>{getUserCredentialsToShow(data, false)}</>;
}

export default function TicketHistoryList() {
    const {
        token: { borderRadius, margin, paddingSM }
    } = theme.useToken();
    const { t: tCommon } = useTranslation('common');

    const { data } = useCurrentTicketEvents({ revalidateOnMount: true });
    const events = useMemo(() => sortEventListByGivenObjectKey(data, 'id'), [data]);
    const { data: fieldList, isLoading } = useTicketFieldDefinitionList();
    const fields = getUIFriendlyTicketFieldDefinitionList(fieldList);

    return (
        <List
            loading={isLoading}
            dataSource={events}
            itemLayout="horizontal"
            renderItem={({ time, comment, browserMetadata, items, userId, id }, index) => {
                const isLastItem = (events ?? []).length > 0 && index === (events ?? []).length - 1;
                return (
                    <CommentAntD
                        author={<Author userId={userId} />}
                        avatar={<ProfilePicture alt="user-image" />}
                        datetime={
                            <Tooltip title={tCommon(getFormattedDateWithHourWithDayjs(time) as any)}>
                                <span>{formatDate(time, { fromNow: true })}</span>
                            </Tooltip>
                        }
                        content={
                            <Space direction="vertical" className="full-width">
                                {comment && (
                                    <Comment
                                        content={comment.value}
                                        // type={comment?.publicVisible ? 'public' : 'internal'}
                                        type="public"
                                        key={id}
                                        id={id}
                                    />
                                )}
                                {items.length > 0 && (
                                    <List
                                        dataSource={items.filter(
                                            // we don't want to show description in history list
                                            (i) => i.fieldDefinitionKey !== TicketSystemFieldsKey.description
                                        )}
                                        itemLayout="horizontal"
                                        split={false}
                                        renderItem={(item) => <Item item={item} fieldListObject={fields.listMap} />}
                                    />
                                )}
                                <BrowserMetaData data={browserMetadata} />
                            </Space>
                        }
                        style={{
                            borderRadius,
                            paddingBottom: 0,
                            paddingLeft: paddingSM,
                            paddingRight: paddingSM,
                            paddingTop: 0,
                            ...(!isLastItem ? { marginBottom: margin / 2 } : {})
                        }}
                    />
                );
            }}
        />
    );
}
